import { z } from 'zod';

import { customerRefSchema } from '@/lib/api/customer-ref';
import { enforceRangePairs } from '@/lib/api/filter-spec';
import { pageMetaSchema } from '@/lib/api/pagination';

/** The four Payment For categories (CONTEXT.md › Payments). "Refund" is a category, not a status. */
export const paymentForSchema = z.enum(['order', 'subscription', 'installation', 'refund']);

/** The ten per-type payment statuses (ADR-0017). `capture_failed` renders as its own table chip
 *  but is folded under "Failed" in the filter. `disputed` (a card chargeback or an ACH reversal)
 *  is set by the backend on `charge.dispute.created` and returns to `success` if the dispute is won. */
export const paymentStatusSchema = z.enum([
  'pending',
  'success',
  'failed',
  'on_hold',
  'debited',
  'released',
  'capture_failed',
  'refund_initiated',
  'refunded',
  'disputed',
]);

/** Structured payment method (ADR-0017): the FE owns the masked display string and filters by
 *  `type`. `card` carries brand/last4 (bankName null); `net_banking` carries bankName (card null). */
export const paymentMethodSchema = z.object({
  type: z.enum(['card', 'net_banking']),
  brand: z.string().nullable(),
  last4: z.string().nullable(),
  bankName: z.string().nullable(),
});

/** One Payments Ledger row = one payment entry, keyed by its own `id`; `orderNumber` is the
 *  repeatable displayed "Order ID" linking sibling entries. */
export const paymentRowSchema = z.object({
  id: z.string(),
  // Order linkage is nullable on the wire (e.g. subscription entries with no order).
  orderId: z.string().nullable(),
  orderNumber: z.string().nullable(),
  // Embedded customer is a nested object (ADR-0020), consistent with the orders list/detail.
  customer: customerRefSchema,
  paidAt: z.string(),
  amount: z.number(),
  status: paymentStatusSchema,
  paymentFor: paymentForSchema,
  transactionId: z.string(),
  deviceCount: z.number(),
  paymentMethod: paymentMethodSchema,
});

export const paymentListResponseSchema = z.object({
  data: z.array(paymentRowSchema),
  meta: pageMetaSchema,
});

/** One event of a payment's timeline (temporary → final). `failureReason` is non-null only on a
 *  Failed / Released / Capture Failed final event; the FE renders it as a red banner. */
export const paymentTimelineEventSchema = z.object({
  status: paymentStatusSchema,
  paymentMethod: paymentMethodSchema,
  timestamp: z.string(),
  failureReason: z.string().nullable(),
});

export const paymentTimelineSchema = z.object({
  header: z.object({
    transactionId: z.string(),
    paymentFor: paymentForSchema,
    customerName: z.string(),
    // Backend omits customerAvatarUrl; orderNumber/orderDate are nullable (non-order entries).
    customerAvatarUrl: z.string().nullish(),
    orderNumber: z.string().nullable(),
    orderDate: z.string().nullable(),
  }),
  events: z.array(paymentTimelineEventSchema),
});

/** RHF draft schema for the filter drawer. Selects are single-value; the status select exposes
 *  the 8 filter options (no `capture_failed`). Numeric ranges are guarded via `enforceRangePairs`;
 *  the payment-date range is guarded in the drawer by disabling To-dates before From. */
export const paymentFiltersSchema = z
  .object({
    // `.nullish()` (null | undefined): the drawer clears a select to `null` (a defined value RHF
    // reliably propagates — clearing to `undefined` doesn't re-render a `defaultValues`-seeded
    // Controller). `toFilters` folds both null and undefined back to "no filter".
    paymentFor: paymentForSchema.nullish(),
    paymentStatus: paymentStatusSchema.exclude(['capture_failed']).nullish(),
    paymentMethod: paymentMethodSchema.shape.type.nullish(),
    deviceCountFrom: z.number().nullable(),
    deviceCountTo: z.number().nullable(),
    amountFrom: z.number().nullable(),
    amountTo: z.number().nullable(),
    paidFrom: z.string().nullable(),
    paidTo: z.string().nullable(),
  })
  .superRefine(
    enforceRangePairs([
      ['amountFrom', 'amountTo'],
      ['deviceCountFrom', 'deviceCountTo'],
    ]),
  );
